import { Box } from "@mui/material"
import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation"
import React, { RefObject, useEffect, useState } from "react"
import { useParams } from "react-router"

import { DGText } from "../../../components/common/DGText"
import { GenericButton } from "../../../components/common/GenericButton"
import { LiveConcert } from "../../../generated/graphql"
import useLiveConcertState from "../../../hooks/use-live-concert-state"
import useSdk, { ErrorLevel } from "../../../hooks/use-sdk"
import useTranslate from "../../../hooks/use-translate"
import { appStore } from "../../../store/AppStore"
import {
  isFavoritedContent,
  PlaybackContent,
  playerStore,
} from "../../../store/PlayerStore"
import { s } from "../../../utilities/common"
import { fontSizeFactor } from "../../../utilities/constants"
import { TI } from "../../../utilities/theme"
import { DetailFocusKeys } from "../detail-focus-keys"
import { useDetailStore } from "../DetailStore"

export const Buttons = ({
  scrollRef,
  accessCheck,
  hasCategoryLinks,
}: {
  scrollRef?: RefObject<HTMLElement | null>
  accessCheck: () => Promise<boolean>
  hasCategoryLinks?: boolean
}) => {
  const t = useTranslate()
  const { focusKey, ref, focusSelf } = useFocusable({
    focusKey: DetailFocusKeys.ButtonGroup,
    isFocusBoundary: true,
    focusBoundaryDirections: hasCategoryLinks
      ? ["left", "right"]
      : ["left", "right", "up"],
    onFocus: (_, __, query) => {
      if (!(query && query.noScroll) && scrollRef?.current)
        scrollRef.current.style.transform = "translate3d(0, 0, 0)"
    },
  })
  const setNavigationProps = playerStore((state) => state.setNavigationProps)
  const setReturnFocus = playerStore((state) => state.setReturnFocus)
  const { type } = useParams()
  const page = useDetailStore((state) => state.page)
  const setInfoOpen = useDetailStore((state) => state.setInfoOpen)
  const setContentPlayer = playerStore((state) => state.setContent)
  const content = useDetailStore((state) => state.content)
  const [ariaLabelPrefix, setAriaLabelPrefix] = useState(
    page?.title + ". " + page?.subtitle + ". ",
  )

  useEffect(() => {
    focusSelf()
  }, [focusSelf])

  return (
    <>
      <FocusContext.Provider value={focusKey}>
        <Box
          ref={ref}
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            // columnGap doesn't work on Tizen
            "& > :not(:last-child)": {
              mr: s("24px"),
            },
          }}
        >
          {page ? (
            <>
              <PlayButton
                onBlur={() => setAriaLabelPrefix("")}
                ariaLabelPrefix={ariaLabelPrefix}
                onPress={async () => {
                  if (!(await accessCheck())) return
                  setReturnFocus(DetailFocusKeys.PlayButton)
                  setContentPlayer(content as PlaybackContent, "0", false)
                  setNavigationProps({
                    open: true,
                    id: page.id,
                    type,
                    isPreview: "false",
                    trackId: "0",
                    startFromProgress: true,
                  })
                }}
              />
              {content?.__typename !== "EditorialPlaylist" && (
                <GenericButton
                  ariaLabel={
                    ariaLabelPrefix + t("component__button_stack_info")
                  }
                  onBlur={() => setAriaLabelPrefix("")}
                  shadow
                  data-testid={"infobutton"}
                  focusKey={DetailFocusKeys.InfoButton}
                  onEnterPress={() => setInfoOpen(true)}
                >
                  <DGText
                    tx={TI.Subheadline07tv}
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {t(
                      content?.__typename === "Video" ||
                        content?.__typename === "VodConcert" ||
                        content?.__typename === "Album" ||
                        content?.__typename === "LiveConcert"
                        ? "component__button_stack_info"
                        : "component__video_player_info_label",
                    )}
                  </DGText>
                </GenericButton>
              )}
              {page.previewButton && (
                <GenericButton
                  ariaLabel={
                    ariaLabelPrefix + t("component__button_stack_preview")
                  }
                  onBlur={() => setAriaLabelPrefix("")}
                  shadow
                  focusKey={DetailFocusKeys.PreviewButton}
                  onEnterPress={() => {
                    setReturnFocus(DetailFocusKeys.PreviewButton)
                    setContentPlayer(content as PlaybackContent, "0", true)
                    setNavigationProps({
                      open: true,
                      id: page.id,
                      type,
                      isPreview: "true",
                      trackId: "0",
                    })
                  }}
                >
                  <DGText
                    tx={TI.Subheadline07tv}
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {t("component__button_stack_preview")}
                  </DGText>
                </GenericButton>
              )}
              <FavoriteButton />
            </>
          ) : null}
        </Box>
      </FocusContext.Provider>
    </>
  )
}

const PlayButton = ({
  onPress,
  ariaLabelPrefix,
  onBlur,
}: {
  onPress: () => void
  ariaLabelPrefix?: string
  onBlur?: () => void
}) => {
  const content = useDetailStore((state) => state.content)
  const t = useTranslate()
  const { status } = useLiveConcertState(
    content && content?.__typename === "LiveConcert"
      ? (content as unknown as LiveConcert)
      : undefined,
  )
  if (
    content?.__typename === "Video" ||
    content?.__typename === "Album" ||
    content?.__typename === "EditorialPlaylist" ||
    content?.__typename === "VodConcert" ||
    status === "live" ||
    status === "pre-live"
  )
    return (
      <GenericButton
        onBlur={onBlur}
        ariaLabel={ariaLabelPrefix + ". " + t("component__button_stack_play")}
        shadow
        focusKey={DetailFocusKeys.PlayButton}
        onEnterPress={onPress}
        data-testid={"playbutton"}
      >
        <svg
          width={s("23")}
          height={s("28")}
          viewBox="0 0 23 28"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4.59629 26.7413C3.77129 27.2613 2.94629 27.2921 2.12129 26.8337C1.29629 26.3754 0.883789 25.6725 0.883789 24.725V3.09132C0.883789 2.14459 1.29629 1.44206 2.12129 0.983723C2.94629 0.52539 3.77129 0.555823 4.59629 1.07502L21.5088 11.8913C22.2722 12.3805 22.6539 13.0606 22.6539 13.9318C22.6539 14.8023 22.2722 15.4667 21.5088 15.925L4.59629 26.7413Z"
            fill="#151F31"
          />
        </svg>
        <DGText
          tx={TI.Subheadline07tv}
          sx={{
            fontSize: s("24px", false, fontSizeFactor),
          }}
        >
          {t("component__button_stack_play")}
        </DGText>
      </GenericButton>
    )
  return null
}

const FavoriteButton = () => {
  const content = useDetailStore((state) => state.content)
  const favorites = useDetailStore((state) => state.favorites)
  const currentUser = appStore((state) => state.currentUser)
  const setMyStage = appStore((state) => state.setMyStage)
  const myStage = appStore((state) => state.myStage)
  const t = useTranslate()
  const sdk = useSdk(ErrorLevel.NoError)

  const [favorited, setFavorited] = useState(false)

  useEffect(() => {
    setFavorited(favorites.content ?? false)
  }, [favorites])

  useEffect(() => {
    // trackId is undefined because the DetailPage favorite button targets content only and not works / tracks
    setFavorited(isFavoritedContent(myStage, content, undefined))
  }, [myStage, content])

  const toggle = async () => {
    setFavorited(!favorited)
    if (content && !favorited)
      sdk.addFavorite({ input: { contentId: content.id } })
    if (content && favorited)
      sdk.removeFavoriteByContentId({ input: { contentId: content.id } })
    // give the server time to update its response
    setTimeout(async () => {
      const result = await sdk.myStagePage({
        sliderPageSize: 7,
        cursor: undefined,
      })
      setMyStage(result)
    }, 1000)
  }

  if (
    currentUser &&
    (content?.__typename === "Album" ||
      content?.__typename === "Video" ||
      content?.__typename === "EditorialPlaylist")
  )
    return (
      <GenericButton
        focusKey={DetailFocusKeys.FavoriteButton}
        onEnterPress={toggle}
      >
        <DGText
          tx={TI.Subheadline07tv}
          sx={{
            fontWeight: 600,
            fontSize: s("24px", false, fontSizeFactor),
          }}
        >
          {t(
            favorited
              ? "contextmenu__remove_favorite"
              : "component__button_stack_fav",
          )}
        </DGText>
      </GenericButton>
    )
  return null
}
